import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { EnhancedError, RequestStatus } from 'src/redux/apiTypes';
import { useAppSelector } from 'src/redux/hooks';
import { openModal } from 'src/redux/modals';
import { useAppConfig } from 'src/utils/appConfig/useAppConfig';
import useSWR from 'swr';
import { useLocalStorage } from 'usehooks-ts';
import { getCommentsMap, getEditedComments, requestDeleteCommentsForPicker, requestGetCommentsForPicker, requestPostCommentsForPicker, requestPutCommentsForPicker } from './helpers';

export interface CommentForPicker {
  ean: string;
  comment: string;
  persisted: boolean;
}

export const COMMENTS_FOR_PICKER = 'commentsForPicker'; // Do not change before migration!
export const swrKey = '/user/comments_for_picker';

export default function useCommentsForPicker() {
  const { fetchConfig } = useAppConfig();

  const dispatch = useDispatch();
  const isLoggedIn = useAppSelector((s) => s.user.isLoggedIn);

  const [localData, setLocalData] = useLocalStorage<CommentForPicker[]>(COMMENTS_FOR_PICKER, []);
  const [hasError, setHasError] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<RequestStatus>('idle');

  const commentsForPickerFetcher = () => requestGetCommentsForPicker(fetchConfig);

  const canFetch = isLoggedIn && !localData?.length;
  const { data: swrData = [], error: swrError, mutate } = useSWR<CommentForPicker[], EnhancedError>(
    canFetch ? swrKey : null,
    commentsForPickerFetcher,
    {
      dedupingInterval: 1000,
      focusThrottleInterval: 1000,
      revalidateOnFocus: true,
    },
  );

  const data = isLoggedIn ? swrData : localData;

  useEffect(() => {
    if (hasError || !!swrError) {
      dispatch(openModal('unexpectedErrorModal'));
      setHasError(false);
    }
  }, [hasError, swrError, dispatch]);

  useEffect(() => {
    async function uploadData() {
      setUploadStatus('loading');
      const response = await requestPostCommentsForPicker(localData, fetchConfig);

      if (!response.ok) {
        setHasError(true);
        setUploadStatus('failed');
        return;
      }

      setLocalData([]);
      setUploadStatus('succeeded');
    }
    if (uploadStatus !== 'loading' && isLoggedIn && localData?.length) {
      uploadData();
    }
  }, [isLoggedIn, localData, mutate, setLocalData, uploadStatus, fetchConfig]);

  const commentsMap = getCommentsMap(data);

  return {
    saveComment,
    deleteComment,
    commentsMap,
    hasError: hasError || !!swrError,
    data,
  };


  async function saveComment(editedComment: CommentForPicker) {
    const {
      comment,
      ean,
    } = editedComment;

    const trimmedNewComment = comment?.trim();
    const commentExists = !!commentsMap[ean];
    const newComment: CommentForPicker = {
      ...editedComment,
      comment: trimmedNewComment,
    };

    if (!commentExists && trimmedNewComment) {
      await createComment(newComment);
    } else if (!trimmedNewComment) {
      await deleteComment(ean);
    } else {
      await editComment(newComment);
    }
  }

  async function editComment(editedComment: CommentForPicker) {
    const newData = getEditedComments(data, editedComment);

    if (isLoggedIn) {
      const response = await requestPutCommentsForPicker(editedComment, fetchConfig);

      if (!response.ok) {
        setHasError(true);
        return;
      }

      mutate(newData, false);
    } else {
      setLocalData(newData);
    }
  }


  async function createComment(newComment: CommentForPicker) {
    const newData = [...data, newComment];

    if (isLoggedIn) {
      const response = await requestPostCommentsForPicker(
        [newComment],
        fetchConfig,
      );

      if (!response.ok) {
        setHasError(true);
        return;
      }

      mutate(newData, false);
    } else {
      setLocalData(newData);
    }
  }

  async function deleteComment(ean: string) {
    const newData = data.filter((d) => d.ean !== ean);

    if (isLoggedIn) {
      const response = await requestDeleteCommentsForPicker(ean, fetchConfig);

      if (!response.ok) {
        setHasError(true);
        return;
      }

      mutate(newData, false);
    } else {
      setLocalData(newData);
    }
  }
}
